import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { ActionableItem } from '../../types';
import ProgressRing from './ProgressRing';

const num = (n: number) => n.toLocaleString('en-IN');

const Fact: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
    <Tooltip title={hint || ''} disableHoverListener={!hint} placement="top" arrow>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#8A8078', mb: 0.25 }}>{label}</Typography>
            {children}
        </Box>
    </Tooltip>
);

const VersionNode: React.FC<{ sku: string; caption: string; tone: 'live' | 'primary' }> = ({ sku, caption, tone }) => (
    <Box sx={{ px: 1.25, py: 0.75, borderRadius: '10px', border: '1.5px solid', minWidth: 0,
        borderColor: tone === 'live' ? 'rgba(34,197,94,0.45)' : 'rgba(220,88,41,0.5)',
        bgcolor: tone === 'live' ? 'rgba(34,197,94,0.08)' : 'rgba(220,88,41,0.07)' }}>
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.78rem', lineHeight: 1.2 }} noWrap>{sku}</Typography>
        <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: tone === 'live' ? '#15803d' : '#B8451E' }}>{caption}</Typography>
    </Box>
);

// Body of a "Move back to Primary" card: a days-of-cover gauge on the left, then the swap (live →
// Primary) and the three facts behind the number.
const MoveBackToPrimaryBody: React.FC<{ item: ActionableItem }> = ({ item }) => {
    const p = item.payload;
    const reduceMotion = useReducedMotion();
    const target = p.targetDoc || 30;
    const comfortable = p.projectedDoc >= target * 2;
    const ringColor = comfortable ? '#16a34a' : '#DC5829';

    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title={`Primary stock ÷ ${p.liveBundleSku}'s demand. Target is ${target} days.`} arrow>
                <Box>
                    <ProgressRing
                        value={p.projectedDoc / (target * 2)}
                        size={92} stroke={9} color={ringColor}
                        label={`${p.projectedDoc.toLocaleString('en-IN')}d`} sublabel={`target ${target}d`}
                        ariaLabel={`Projected ${p.projectedDoc} days of cover against a target of ${target}`}
                    />
                </Box>
            </Tooltip>

            <Box sx={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <VersionNode sku={p.liveBundleSku} caption="Live now" tone="live" />
                    <Box sx={{ position: 'relative', flex: '0 0 44px', height: 2, bgcolor: 'rgba(26,20,15,0.12)', borderRadius: 1, overflow: 'hidden', mx: 0.25 }} aria-hidden>
                        {!reduceMotion && (
                            <motion.span
                                animate={{ x: ['-100%', '100%'] }}
                                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                                style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, #DC5829, transparent)' }}
                            />
                        )}
                    </Box>
                    <VersionNode sku={p.primaryBundleSku} caption={p.primaryIsFuture ? 'Primary · queued' : 'Primary · not live'} tone="primary" />
                </Box>

                <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                    <Fact label="Demand" hint={p.liveDrrInherited ? `Carried over from an earlier version of ${p.liveBundleSku}` : `7-day demand of ${p.liveBundleSku}`}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', fontVariantNumeric: 'tabular-nums' }}>{p.liveDrr}<Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600 }}>/day{p.liveDrrInherited ? ' · carried over' : ''}</Typography></Typography>
                    </Fact>
                    <Fact label="Primary stock" hint="Units already made plus how many the child SKUs allow it to be made">
                        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', fontVariantNumeric: 'tabular-nums' }}>{num(p.potentialUnits)}<Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600 }}> ({num(p.primaryInventory)} made + {num(p.primaryCanBeMade)} makeable)</Typography></Typography>
                    </Fact>
                </Box>

                {Array.isArray(p.limitingChildren) && p.limitingChildren.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 700 }}>Limited by</Typography>
                        {p.limitingChildren.map((c: { sku: string; inventory: number }) => (
                            <Chip key={c.sku} size="small" variant="outlined" label={`${c.sku} · ${num(c.inventory)}`} sx={{ height: 20, fontFamily: 'monospace', fontSize: '0.64rem', borderColor: '#DC5829', color: '#B8451E' }} />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default MoveBackToPrimaryBody;
